import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Check } from "lucide-react";
import { useState } from "react";

export const LandingPricing = () => {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual">("monthly");

  const planDetails = {
    monthly: {
      price: "R$ 16,00",
      period: "/mês",
      description: "Invista na sua clareza mental por menos de um lanche de aplicativo por mês.",
    },
    annual: {
      price: "R$ 150,00",
      period: "/ano",
      description: "Equivalente a R$ 12,50 por mês • Economize 22%",
    },
  };

  const features = [
    "Acesso a todos os recursos da KopfAI",
    "Converse sem interrupções",
    "Gere relatórios",
    "Suporte prioritário",
  ];

  return (
    <section className="relative w-full min-h-[659px] py-16 md:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#FFD166]/8 to-white -z-10"></div>

      {/* Animated Blobs */}
      <div
        className="absolute top-0 left-[15%] w-[500px] h-[500px] rounded-full opacity-10 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.4) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "float 25s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-0 right-[10%] w-[600px] h-[600px] rounded-full opacity-12 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "float 30s ease-in-out infinite reverse",
        }}
      ></div>

      <div
        className="text-center mb-8"
        style={{
          animation: "fadeInUp 0.8s ease-out",
        }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-2">Escolha o ritmo ideal para sua jornada</h2>
        <p className="text-base md:text-lg text-[#272D37]/70 mb-1">Sua mente mais leve não tem preço.</p>
        <p className="text-base md:text-lg text-[#272D37]/70 mb-1">
          Acesso total à KopfAI. Você só escolhe a frequência do pagamento.
        </p>
      </div>

      <div className="max-w-md mx-auto">
        {/* Plan Toggle */}
        <div
          className="mb-6"
          style={{
            animation: "fadeInUp 0.8s ease-out 0.2s backwards",
          }}
        >
          <div className="inline-flex rounded-lg bg-[#4A4E69]/10 p-1 w-full backdrop-blur-sm">
            <button
              onClick={() => setSelectedPlan("monthly")}
              className={`flex-1 rounded-md px-4 py-3 text-sm font-medium transition-all ${
                selectedPlan === "monthly"
                  ? "bg-white text-[#4A4E69] shadow-md"
                  : "text-[#4A4E69]/70 hover:text-[#4A4E69]"
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setSelectedPlan("annual")}
              className={`flex-1 rounded-md px-4 py-3 text-sm font-medium transition-all ${
                selectedPlan === "annual"
                  ? "bg-white text-[#4A4E69] shadow-md"
                  : "text-[#4A4E69]/70 hover:text-[#4A4E69]"
              }`}
            >
              Anual
            </button>
          </div>
        </div>

        {/* Plan Card */}
        <div
          className="group relative"
          style={{
            animation: "fadeInUp 0.8s ease-out 0.4s backwards",
          }}
        >
          <div className="relative w-full bg-white/60 backdrop-blur-xl rounded-3xl border-2 border-[#FFD166]/50 p-8 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
            {/* Glow effect on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-2xl"
              style={{
                background: "radial-gradient(circle at 50% 0%, rgba(255, 209, 102, 0.3), transparent 70%)",
              }}
            ></div>

            {/* Content */}
            <div className="relative z-10">
              {/* Price */}
              <div className="mb-3">
                <span className="text-4xl md:text-5xl font-extrabold text-[#4A4E69]">
                  {planDetails[selectedPlan].price}
                </span>
                <span className="text-xl md:text-2xl text-[#272D37]/70 font-medium ml-2">
                  {planDetails[selectedPlan].period}
                </span>
              </div>

              <p className="text-sm text-[#272D37]/75 mb-6">{planDetails[selectedPlan].description}</p>

              {/* Features */}
              <div className="space-y-3 pt-5 mb-6 border-t border-[#FFD166]/20">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-[#FFD166]/20 p-1">
                      <Check className="h-3 w-3 text-[#4A4E69]" />
                    </div>
                    <p className="flex-1 text-sm text-[#272D37]">{feature}</p>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link to="/auth?tab=signup" className="w-full">
                <Button className="w-full h-[53px] rounded-full text-lg font-bold bg-transparent border-2 border-[#4A4E69] text-[#4A4E69] hover:bg-[#4A4E69] hover:text-white transition-all duration-300 hover:shadow-xl hover:scale-105">
                  Testar grátis agora
                </Button>
              </Link>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FFD166] to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500 rounded-b-3xl"></div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(40px, 40px) scale(1.12); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </section>
  );
};
